import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useChecklistMaquinaEquipItensCadastroPagination } from "../hooks/ChecklistMaquinaEquipItens_cadastro/pagination";
import { ChecklistMaquinaEquipModel } from "../../domain/models/checklist_maquina_equip";
import { ChecklistMaquinaEquipItemModel } from "../../domain/models/checklist_maquina_equip_item";
import { ItemChecklistMaquinaEquipForm } from "../schemas/ChecklistMaquinaEquipItens_cadastro/item_checklist_maquina_equip";

/**
 * LoadItensChecklistMaquinaEquipFuncState
 */

type LoadItensChecklistMaquinaEquipFuncState = {
  idChecklistMaquinaEquip?: number;
  ordem?: string;
  descricao?: string;

  page?: number;
  rowsPerPage?: number;
};

/**
 * useChecklistServicoItensCadastroViewModel
 */

export const useChecklistMaquinaEquipItensCadastroViewModel = () => {
  const {
    controlePatrimonio: {
      getAllChecklistMaquinaEquipItensUseCase,
      getChecklistMaquinaEquipByIdUseCase,
      getChecklistMaquinaEquipItensByIdUseCase,
      createChecklistMaquinaEquipItensUseCase,
      updateChecklistMaquinaEquipItensUseCase,
      deleteChecklistMaquinaEquipItensUseCase,
    }
  } = useDependencyInjection();

  const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageSaving } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idChecklistMaquinaEquip, setIdChecklistMaquinaEquip] = useState<number>();
  const [checklistMaquinaEquip, setChecklistMaquinaEquip] = useState<ChecklistMaquinaEquipModel>();

  const [selectedItemChecklistMaquinaEquip, setSelectedItemChecklistMaquinaEquip] = useState<ChecklistMaquinaEquipItemModel>();

  const [listaChecklistMaquinaEquipItens, setListaChecklistMaquinaEquipItens] = useState<ChecklistMaquinaEquipItemModel[]>();

  const [itemChecklistMaquinaEquipModal, setItemChecklistMaquinaEquipModal] = useState(false);
  const [itemChecklistMaquinaEquipDeleteModal, setItemChecklistMaquinaEquipDeleteModal] = useState(false);
  const [selectedIdItemDelete, setSelectedIdItemDelete] = useState<number | undefined>();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

  const pagination = useChecklistMaquinaEquipItensCadastroPagination();

  /**
   * Funcoes de carregamento
   */

  const loadChecklistMaquinaEquip = useCallback(async (id: number) => {
    try {
      const result = await getChecklistMaquinaEquipByIdUseCase.execute(
        id,
      );
      setChecklistMaquinaEquip(result);
    } catch (e: unknown) {
      setChecklistMaquinaEquip(undefined);
      throw new Error('Falha ao buscar checklist de máquina/equipamento!');
    }
  }, [
    getChecklistMaquinaEquipByIdUseCase,
  ]);

  const loadItensChecklistMaquinaEquip = useCallback(async (fs?: LoadItensChecklistMaquinaEquipFuncState) => {
    try {
      const filteredIdChecklistMaquinaEquip = fs?.idChecklistMaquinaEquip ?? idChecklistMaquinaEquip;

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;
      const filteredOrdem = fs?.ordem;
      const filteredDescricao = fs?.descricao;

      await getAllChecklistMaquinaEquipItensUseCase.execute(
        filteredIdChecklistMaquinaEquip ?? 0,
        filteredOrdem ?? null,
        filteredDescricao ?? null,
        filteredPage,
        filteredRowsPerPage,

      );

      setListaChecklistMaquinaEquipItens(getAllChecklistMaquinaEquipItensUseCase.items);
      pagination.setTotalItems(getAllChecklistMaquinaEquipItensUseCase.totalItems);
    } catch (e: unknown) {
      setListaChecklistMaquinaEquipItens(undefined);
      throw new Error('Falha ao buscar itens do checklist de máquina/equipamento!');
    }
  }, [
    idChecklistMaquinaEquip,
    getAllChecklistMaquinaEquipItensUseCase,
    pagination,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationIdChecklistMaquinaEquip = params.get('id');
      const locIdChecklistMaquinaEquip = locationIdChecklistMaquinaEquip ?? undefined;

      if (!locIdChecklistMaquinaEquip || locIdChecklistMaquinaEquip.length === 0) {
        setErrorMessage("Falha ao recuperar id de checklist de máquina/equipamento!");
        return;
      }
      setIdChecklistMaquinaEquip(Number(locIdChecklistMaquinaEquip));

      await loadChecklistMaquinaEquip(Number(locIdChecklistMaquinaEquip));

      await loadItensChecklistMaquinaEquip({ idChecklistMaquinaEquip: Number(locIdChecklistMaquinaEquip) });
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/ChecklistMaquinaEquip_lista' });
  }, [
    backOrNavigate
  ]);

  const handleReload = useCallback(async () => {
    if (idChecklistMaquinaEquip) {
      try {
        setLoading(true);

        await loadChecklistMaquinaEquip(idChecklistMaquinaEquip);

        await loadItensChecklistMaquinaEquip({ idChecklistMaquinaEquip });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados pagina!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    idChecklistMaquinaEquip,
    loadChecklistMaquinaEquip,
    loadItensChecklistMaquinaEquip,
    pushErrorNotification,
  ]);

  const handleItemChecklistMaquinaEquipSubmit = useCallback(async (form: ItemChecklistMaquinaEquipForm) => {
    if (idChecklistMaquinaEquip) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedItemChecklistMaquinaEquip) {
          await updateChecklistMaquinaEquipItensUseCase.execute(
            selectedItemChecklistMaquinaEquip.idItemChecklistMaquinaEquip ?? 0,
            form.ordem,
            form.descricao,
          );
        } else {
          await createChecklistMaquinaEquipItensUseCase.execute(
            idChecklistMaquinaEquip,
            form.ordem,
            form.descricao,
          );
        }
        await loadItensChecklistMaquinaEquip();
        setItemChecklistMaquinaEquipModal(false);
        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao salvar item de checklist de serviço!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idChecklistMaquinaEquip,
    selectedItemChecklistMaquinaEquip,
    createChecklistMaquinaEquipItensUseCase,
    updateChecklistMaquinaEquipItensUseCase,
    loadItensChecklistMaquinaEquip,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleItemChecklistMaquinaEquipDeleteSubmit = useCallback(async () => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedIdItemDelete) {
        await deleteChecklistMaquinaEquipItensUseCase.execute(
          [selectedIdItemDelete ?? 0],
        );
      }
      await loadItensChecklistMaquinaEquip();
      setItemChecklistMaquinaEquipDeleteModal(false);
      pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao excluir item de checklist de máquina/equipamento!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedIdItemDelete,
    deleteChecklistMaquinaEquipItensUseCase,
    loadItensChecklistMaquinaEquip,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useChecklistMaquinaEquipItensCadastroPagination.setState({ page: page + 1 });
      await loadItensChecklistMaquinaEquip({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadItensChecklistMaquinaEquip,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useChecklistMaquinaEquipItensCadastroPagination.setState({ page: 1, rowsPerPage });
      await loadItensChecklistMaquinaEquip({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar dados pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadItensChecklistMaquinaEquip,
    pushErrorNotification,
  ]);

  const handleItemChecklistMaquinaEquipModalOpenState = useCallback((idItemChecklistMaquinaEquip: number | null, openState: boolean) => {
    if (openState) {
      if (idItemChecklistMaquinaEquip && listaChecklistMaquinaEquipItens) {
        setSelectedItemChecklistMaquinaEquip(listaChecklistMaquinaEquipItens.find(x => x.idItemChecklistMaquinaEquip === idItemChecklistMaquinaEquip));
      } else {
        setSelectedItemChecklistMaquinaEquip(undefined);
      }
    }
    setItemChecklistMaquinaEquipModal(openState);
  }, [
    listaChecklistMaquinaEquipItens,
  ]);

  const handleItemChecklistMaquinaEquipDeleteModalOpenState = useCallback((idItemChecklistMaquinaEquip: number | null, openState: boolean) => {
    if (openState) {
      if (idItemChecklistMaquinaEquip) {
        setSelectedIdItemDelete(idItemChecklistMaquinaEquip);
      } else {
        setSelectedIdItemDelete(undefined);
      }
    }
    setItemChecklistMaquinaEquipDeleteModal(openState);
  }, []);

  const handleRowSelectionModelChange = useCallback((newRowSelectionModel: GridRowSelectionModel) => {
    setRowSelectionModel(newRowSelectionModel);
  }, [
  ]);

  return {
    loading,
    saving,
    errorMessage,
    idChecklistMaquinaEquip,
    selectedItemChecklistMaquinaEquip: selectedItemChecklistMaquinaEquip ?? null,
    listaChecklistMaquinaEquipItens: listaChecklistMaquinaEquipItens ?? [],
    itemChecklistMaquinaEquipModal,
    itemChecklistMaquinaEquipDeleteModal,
    selectedIdItemDelete,
    checklistMaquinaEquip,
    rowSelectionModel,
    pagination,

    handleBack,
    handleReload,
    handleItemChecklistMaquinaEquipDeleteSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleItemChecklistMaquinaEquipSubmit,
    handleItemChecklistMaquinaEquipModalOpenState,
    handleItemChecklistMaquinaEquipDeleteModalOpenState,
    handleRowSelectionModelChange,
  };
}